import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';

import { type LegalDocumentGroup } from '@/modules/legalDocuments';
import { useRulesEngine } from '@/modules/rulesEngine';

import {
  type AccountFormValues,
  type accountSchema,
  type purlAccountSchema,
  type ssoAccountSchema,
  type ssoWithPasswordAccountSchema,
} from '../schema/accountSchema';
import { mapDraftToFormValues } from '../utils/mapDraftToFormValues';
import { mapFormValuesToDraft } from '../utils/mapFormValuesToDraft';

// The per-flow schemas all share the AccountFormValues shape (they only relax
// validation on hidden fields), so they're interchangeable here. Typed as the
// concrete union — not a widened `z.ZodType` — so zodResolver keeps the precise
// input type useForm<AccountFormValues> expects.
type AccountSchema =
  | typeof accountSchema
  | typeof ssoAccountSchema
  | typeof ssoWithPasswordAccountSchema
  | typeof purlAccountSchema;

export type UseAccountForm = UseFormReturn<AccountFormValues> & {
  // Merges the current form values into the draft (the shared first step of every
  // flow's submit) so a mid-flow back/return restores what was typed.
  persistDraft: (values: AccountFormValues) => void;
};

// Shared react-hook-form setup for the account-creation flows: seeds defaults
// from the draft, keeps the form in sync when the draft's birthday hydrates
// (mapUserDataToDraft on a returning user), and exposes `persistDraft`. Each flow
// passes its own zod schema (they share the AccountFormValues shape; only hidden
// fields' validation differs).
export function useAccountForm(
  schema: AccountSchema,
  legalGroups: LegalDocumentGroup[]
): UseAccountForm {
  const { draft, setDraft } = useRulesEngine();

  const formDefaultValues = useMemo(
    () => mapDraftToFormValues(draft, legalGroups),
    [draft, legalGroups]
  );

  const form = useForm<AccountFormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: formDefaultValues,
  });
  const { reset } = form;

  const persistDraft = useCallback(
    (values: AccountFormValues) => {
      setDraft((prev) => ({ ...prev, ...mapFormValuesToDraft(values, legalGroups) }));
    },
    [setDraft, legalGroups]
  );

  useEffect(() => {
    if (draft.birthday) {
      reset(mapDraftToFormValues(draft, legalGroups), { keepDefaultValues: true });
    }
  }, [draft, legalGroups, reset]);

  return { ...form, persistDraft };
}
